/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2020, Zumper
 * @description  ExpirableLocalStorage.ts
 *               Thin wrapper around localStorage that enforces a TTL
 */

import { getExpiresInMs } from '@zcode/expirable-lru'
import { EnhancedLocalStorage } from './EnhancedLocalStorage'
import { toStoredData } from './helpers/toStoredData'
import { toValue } from './helpers/toValue'
import { logger } from './logger'
import { ExpirableStorage } from './types/ExpirableStorage'
import { ExpirableStorageOptions } from './types/ExpirableStorageOptions'
import { StoredData } from './types/StoredData'

export type ExpirableLocalStorageOptions = ExpirableStorageOptions

export class ExpirableLocalStorage<TValue = any>
  implements ExpirableStorage<TValue>
{
  protected options: ExpirableLocalStorageOptions
  protected storage: EnhancedLocalStorage<StoredData<TValue>>
  constructor(options: ExpirableLocalStorageOptions) {
    this.storage = new EnhancedLocalStorage({
      name: options.name,
      keySeparator: options.keySeparator,
    })
    this.options = { ...options }
  }

  get length() {
    return this.storage.length
  }

  get version() {
    return this.options.version
  }

  set version(value: ExpirableLocalStorageOptions['version']) {
    this.options.version = value
  }

  clear() {
    this.storage.clear()
  }

  getExpiresInMs(key: string) {
    const data = this.storage.getItem(key)
    if (data === undefined || data === null) {
      return null
    }
    return getExpiresInMs(data, this.options)
  }

  getItem(key: string) {
    const data = this.storage.getItem(key)
    if (data === undefined || data === null) {
      return null
    }
    try {
      return toValue<TValue>(data, this.options)
    } catch (error) {
      /* istanbul ignore next */
      logger.error(error)
      /* istanbul ignore next */
      return null
    }
  }

  key(index: number) {
    return this.storage.key(index)
  }

  removeItem(key: string) {
    this.storage.removeItem(key)
  }

  setItem(key: string, value: TValue, ttl?: number) {
    try {
      const options =
        ttl !== undefined ? { ...this.options, ttl } : this.options
      const data = toStoredData(value, options)
      this.storage.setItem(key, data)
    } catch (error) {
      /* istanbul ignore next */
      logger.error(error)
    }
  }
}
